import React, { useState, useEffect } from 'react';
import { Phone, Calendar, Activity, TrendingUp, ExternalLink, Filter, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Card from '../components/ui/Card';
import StatCard from '../components/ui/StatCard';
import SortableTable from '../components/ui/SortableTable';
import StatusBadge from '../components/ui/StatusBadge';
import DateRangePicker from '../components/ui/DateRangePicker';
import { getAppointments, getProspectingDays, getClients } from '../lib/api';
import { getAccessibleClientIds, getUserAccessibleClients } from '../lib/multiClientUtils';
import { formatDateTime, formatDate } from '../lib/utils';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useAuth } from '../components/AuthProvider';

const DashboardPage: React.FC = () => {
  const { profile } = useAuth();
  const [stats, setStats] = useState({
    totalCalls: 0,
    totalAppointments: 0,
    totalConversations: 0,
    globalConversionRate: 0,
  });
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [recentProspectingDays, setRecentProspectingDays] = useState([]);
  const [filteredProspectingDaysForChart, setFilteredProspectingDaysForChart] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{ start: string | null; end: string | null }>({
    start: null,
    end: null
  });
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<any>(null);

  // Filter data based on date range
  const filterDataByDateRange = (data: any[], dateField: string) => {
    if (!dateRange.start || !dateRange.end) return data;
    
    return data.filter(item => {
      const itemDate = new Date(item[dateField]);
      const startDate = new Date(dateRange.start!);
      const endDate = new Date(dateRange.end!);
      
      return itemDate >= startDate && itemDate <= endDate;
    });
  };

  const handleDateRangeChange = (startDate: string | null, endDate: string | null) => {
    setDateRange({ start: startDate, end: endDate });
  };

  useEffect(() => {
    const fetchClients = async () => {
      if (profile) {
        try {
          const clientsData = await getUserAccessibleClients(profile.user_id, profile.role);
          setClients(clientsData);
        } catch (error) {
          console.error('Error fetching clients:', error);
        }
      }
    };
    fetchClients();
  }, [profile]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let clientIds: string[] = [];

        if (selectedClientId) {
          clientIds = [selectedClientId];
          const client = clients.find(c => c.id === selectedClientId);
          setSelectedClient(client);
        } else {
          setSelectedClient(null);
          if (profile) {
            clientIds = await getAccessibleClientIds(profile.user_id, profile.role);
          }
        }

        // Fetch all data
        const [allAppointments, allProspectingDays] = await Promise.all([
          getAppointments(clientIds, { limit: 100 }),
          getProspectingDays(clientIds, { limit: 100 })
        ]);
        
        // Filter data based on date range
        const filteredAppointments = filterDataByDateRange(allAppointments, 'created_at');
        const filteredProspectingDays = filterDataByDateRange(allProspectingDays, 'date');
        
        // Store filtered prospecting days for chart
        setFilteredProspectingDaysForChart(filteredProspectingDays);
        
        // Calculate stats from filtered data
        const totalCalls = filteredProspectingDays.reduce((sum, day) => sum + (day.calls_count || 0), 0);
        const totalConversations = filteredProspectingDays.reduce((sum, day) => sum + (day.conversations_count || 0), 0);
        const totalAppointments = filteredAppointments.length;
        const totalRdvsBooked = filteredProspectingDays.reduce((sum, day) => sum + (day.rdvs_booked || 0), 0);
        
        // Calculate global conversation rate (RDVs booked / Total conversations)
        const globalConversationRate = totalConversations > 0 ? (totalRdvsBooked / totalConversations) * 100 : 0;
        
        setStats({
          totalCalls,
          totalAppointments,
          totalConversations,
          globalConversionRate: globalConversationRate
        });
        
        // Set recent appointments (latest 4 by creation date)
        const sortedAppointments = filteredAppointments
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 4);
        setRecentAppointments(sortedAppointments);

        // Set recent prospecting days (latest 2 by prospecting date)
        const sortedProspectingDays = filteredProspectingDays
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 2);
        setRecentProspectingDays(sortedProspectingDays);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Erreur lors du chargement des données du tableau de bord');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange, selectedClientId, profile, clients]);

  const getStatusBadge = (status: string) => {
    return <StatusBadge status={status} size="sm" />;
  };

  // Reorganized appointment columns - Title first, Created last
  const appointmentColumns = [
    {
      key: 'title',
      label: 'Title',
      render: (value: string, row: any) => (
        <Link 
          to={`/appointments/${row.id}`}
          className="text-pink-600 hover:text-pink-800 font-medium"
        >
          {value}
        </Link>
      )
    },
    {
      key: 'prospect_name',
      label: 'Prospect',
      render: (value: string, row: any) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.job_title && (
            <div className="text-xs text-gray-500">{row.job_title}</div>
          )}
        </div>
      )
    },
    {
      key: 'date',
      label: 'Appointment Date',
      render: (value: string, row: any) => formatDateTime(value, row.time)
    },
    {
      key: 'company_name',
      label: 'Company',
      render: (value: string) => value || 'N/A'
    },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => getStatusBadge(value)
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value: string) => formatDate(value)
    }
  ];

  const prospectingColumns = [
    {
      key: 'date',
      label: 'Prospecting Date',
      render: (value: string) => formatDate(value)
    },
    {
      key: 'calls_count',
      label: 'Contacts Made',
      render: (value: number) => value || 0
    },
    {
      key: 'clients',
      label: 'Client/Area',
      render: (value: any) => value?.name || 'N/A'
    },
    {
      key: 'rdvs_booked',
      label: 'RDVs Booked',
      render: (value: number) => (
        <span className={`font-medium ${value > 0 ? 'text-green-600' : 'text-gray-500'}`}>
          {value || 0}
        </span>
      )
    },
    {
      key: 'conversion_rate',
      label: 'Conversion Rate',
      render: (value: any, row: any) => {
        const rate = row.calls_count > 0 
          ? ((row.rdvs_booked || 0) / row.calls_count * 100).toFixed(1)
          : '0.0';
        return `${rate}%`;
      }
    },
    {
      key: 'id',
      label: 'Actions',
      sortable: false,
      render: (value: string) => (
        <Link 
          to={`/prospecting/${value}`}
          className="text-pink-600 hover:text-pink-800 flex items-center"
        >
          <ExternalLink className="h-4 w-4" />
        </Link>
      )
    }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" text="Chargement du tableau de bord..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Date Filter */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-gray-900 to-pink-600 bg-clip-text text-transparent">
              Dashboard
            </h1>
            <p className="text-gray-600 mt-2 text-lg">Vue d'ensemble de votre activité Hook Lab</p>
          </div>

          <div className="flex items-center space-x-3">
            {profile?.role === 'admin' && clients.length > 0 && (
              <div className="flex items-center space-x-2">
                <Users className="h-5 w-5 text-gray-500" />
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="rounded-xl border border-gray-300 px-4 py-2 focus:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition-all duration-200"
                >
                  <option value="">Tous les clients</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Filter className="h-5 w-5 text-gray-500" />
            <DateRangePicker onDateRangeChange={handleDateRangeChange} />
          </div>
        </motion.div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Client and Date Range Indicators */}
        {selectedClient && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4"
          >
            <p className="text-sm text-blue-700 flex items-center">
              <Users className="h-4 w-4 mr-2" />
              Statistiques pour: <span className="font-semibold ml-1">{selectedClient.name}</span>
              <span className="text-blue-500 mx-2">•</span>
              <span className="text-blue-600">{selectedClient.company_name}</span>
            </p>
          </motion.div>
        )}
        {(dateRange.start && dateRange.end) && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-xl p-4"
          >
            <p className="text-sm text-pink-700 flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              Données filtrées du {new Date(dateRange.start).toLocaleDateString('fr-FR')} au {new Date(dateRange.end).toLocaleDateString('fr-FR')}
            </p>
          </motion.div>
        )}
        
        {/* Stats Grid */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8"
        >
          <StatCard
            title="Total Calls"
            value={stats.totalCalls}
            icon={<Phone className="h-5 w-5 text-pink-600" />}
            className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200"
          />
          <StatCard
            title="Total Appointments"
            value={stats.totalAppointments}
            icon={<Calendar className="h-5 w-5 text-pink-600" />}
            className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200"
          />
          <StatCard
            title="Total Conversations"
            value={stats.totalConversations}
            icon={<Activity className="h-5 w-5 text-pink-600" />}
            className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200"
          />
          <StatCard
            title="Global Conversation Rate"
            value={`${stats.globalConversionRate.toFixed(1)}%`}
            icon={<TrendingUp className="h-5 w-5 text-pink-600" />}
            className="bg-gradient-to-br from-pink-50 to-pink-100 border border-pink-200"
          />
        </motion.div>
        
        {/* Call Sessions Chart */}
        
        {/* Latest Prospecting Sessions Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <Card title="Latest Prospecting Sessions" className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
            <div className="mb-4 hidden sm:block">
              <p className="text-sm text-gray-600">
                {dateRange.start && dateRange.end 
                  ? `Dernières sessions de prospection dans la période sélectionnée`
                  : `Dernières 2 sessions de prospection (toutes données)`
                }
              </p>
            </div>
            {isLoading && (
              <div className="flex justify-center py-8">
                <LoadingSpinner text="Chargement des sessions..." />
              </div>
            )}
            <div className="overflow-x-auto">
              <SortableTable
                data={recentProspectingDays}
                columns={prospectingColumns}
                defaultSort={{ key: 'date', direction: 'desc' }}
                className="min-w-[600px]"
              />
            </div>
            {!isLoading && recentProspectingDays.length === 0 && (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">
                  {dateRange.start && dateRange.end 
                    ? 'Aucune session de prospection dans cette période'
                    : 'Aucune session de prospection trouvée'
                  }
                </p>
              </div>
            )}
          </Card>
        </motion.div>

        {/* Recent Appointments Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card title="Recent Appointments" className="bg-white/80 backdrop-blur-sm border-gray-200 shadow-lg">
            <div className="mb-4 hidden sm:block">
              <p className="text-sm text-gray-600">
                {dateRange.start && dateRange.end 
                  ? `Derniers rendez-vous créés dans la période sélectionnée`
                  : `Derniers 4 rendez-vous créés (toutes données)`
                }
              </p>
            </div>
            {isLoading && (
              <div className="flex justify-center py-8">
                <LoadingSpinner text="Chargement des rendez-vous..." />
              </div>
            )}
            <div className="overflow-x-auto">
              <SortableTable
                data={recentAppointments}
                columns={appointmentColumns}
                defaultSort={{ key: 'created_at', direction: 'desc' }}
                className="min-w-[700px]"
              />
            </div>
            {!isLoading && recentAppointments.length === 0 && (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">
                  {dateRange.start && dateRange.end 
                    ? 'Aucun rendez-vous dans cette période'
                    : 'Aucun rendez-vous trouvé'
                  }
                </p>
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;